// Search-engine metadata for the public pages, shared by the build and the
// running app so the two can never disagree.
//
// Why the build needs it: GitHub Pages has no SPA fallback, so a URL with no
// file behind it (`/sets`, `/privacy`) is answered by public/404.html with a
// **404 status** and a JS redirect. Browsers never notice; search engines read
// the 404 and drop the page. Until 2026-11-04 the homepage was the only URL on
// the site that could be indexed. The `deckloom-seo-pages` plugin in
// vite.config.js now writes a `<path>.html` copy of index.html for every entry
// below — Pages serves `/sets` from `sets.html` with a 200 — with that page's
// own title, description and canonical baked in, and emits sitemap.xml from
// the same list.
//
// Why the client needs it: the head of whichever HTML the visitor landed on
// stays put across SPA navigation. `RouteHeadSync` (App.jsx) rewrites it on
// every route change so the tab title and canonical follow the route.
//
// Pure module — no imports — because vite.config.js loads it in Node.

export const SITE_ORIGIN = 'https://deckloom.app'

export const DEFAULT_TITLE = 'DeckLoom'

export const PUBLIC_PAGES = [
  {
    path: '/',
    title: 'DeckLoom — free MTG collection tracker and deck builder',
    description: 'DeckLoom is a free Magic: The Gathering collection tracker. Scan unlimited cards with your camera — no scan caps, no paywalls — then build decks, organise binders and wishlists, and follow daily EUR and USD market prices.',
    priority: '1.0',
    changefreq: 'weekly',
  },
  {
    path: '/sets',
    title: 'Upcoming Magic Sets — DeckLoom',
    description: 'Release dates and countdowns for upcoming Magic: The Gathering sets, with a spoiler page for each set that fills in as new cards are previewed.',
    priority: '0.8',
    changefreq: 'daily',
  },
  {
    path: '/legal',
    title: 'Legal and transparency — DeckLoom',
    description: "How DeckLoom works: its privacy model, what the browser stores, the third-party sources it relies on, and how to request account deletion.",
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/terms',
    title: 'Terms of Service — DeckLoom',
    description: 'The agreement for using DeckLoom, the free Magic: The Gathering collection tracker and deck builder.',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy — DeckLoom',
    description: 'What data DeckLoom stores to sync your Magic: The Gathering collection, why, who processes it, and how to get it deleted.',
    priority: '0.3',
    changefreq: 'yearly',
  },
  {
    path: '/storage',
    title: 'Cookies and local storage — DeckLoom',
    description: 'What DeckLoom keeps in your browser: session, settings and a local collection cache. No advertising or tracking cookies.',
    priority: '0.2',
    changefreq: 'yearly',
  },
  {
    path: '/credits',
    title: 'Credits and fan content notice — DeckLoom',
    description: 'The card data, price and image sources DeckLoom is built on, and its status as unofficial Magic: The Gathering fan content.',
    priority: '0.2',
    changefreq: 'yearly',
  },
]

/** `/sets/` and `/sets` are one page; the root stays `/`. */
export function normalizePath(pathname = '/') {
  const trimmed = String(pathname).replace(/\/+$/, '')
  return trimmed || '/'
}

export function findPageMeta(pathname) {
  const path = normalizePath(pathname)
  return PUBLIC_PAGES.find(page => page.path === path) || null
}

// Public pages with dynamic ids, so not prerendered: per-set spoiler pages,
// and shared decks (crawlers get the og-worker's HTML for those anyway).
const DYNAMIC_PUBLIC_PATHS = [/^\/sets\/[a-z0-9]+$/i, /^\/d\/[^/]+$/]

/** A public page whose canonical is itself; everything else points at `/`. */
export function isIndexablePath(pathname) {
  const path = normalizePath(pathname)
  return Boolean(findPageMeta(path)) || DYNAMIC_PUBLIC_PATHS.some(re => re.test(path))
}

export function canonicalUrlFor(pathname) {
  const path = normalizePath(pathname)
  return path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`
}

/** The file the build writes for a page: `/sets` → `sets.html`. */
export function htmlFileFor(path) {
  return path === '/' ? 'index.html' : `${normalizePath(path).slice(1)}.html`
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Each tag must exist exactly where index.html puts it. A miss throws, so an
// edit to index.html that renames or drops one fails the build instead of
// silently shipping every page with the homepage's head.
const HEAD_TAGS = [
  { name: 'title', pattern: /<title>[^<]*<\/title>/, render: page => `<title>${escapeHtml(page.title)}</title>` },
  { name: 'description', pattern: /<meta name="description" content="[^"]*"\s*\/?>/, render: page => `<meta name="description" content="${escapeHtml(page.description)}" />` },
  { name: 'canonical', pattern: /<link rel="canonical" href="[^"]*"\s*\/?>/, render: page => `<link rel="canonical" href="${canonicalUrlFor(page.path)}" />` },
  { name: 'og:url', pattern: /<meta property="og:url" content="[^"]*"\s*\/?>/, render: page => `<meta property="og:url" content="${canonicalUrlFor(page.path)}" />` },
  { name: 'og:title', pattern: /<meta property="og:title" content="[^"]*"\s*\/?>/, render: page => `<meta property="og:title" content="${escapeHtml(page.title)}" />` },
  { name: 'og:description', pattern: /<meta property="og:description" content="[^"]*"\s*\/?>/, render: page => `<meta property="og:description" content="${escapeHtml(page.description)}" />` },
  { name: 'twitter:title', pattern: /<meta name="twitter:title" content="[^"]*"\s*\/?>/, render: page => `<meta name="twitter:title" content="${escapeHtml(page.title)}" />` },
  { name: 'twitter:description', pattern: /<meta name="twitter:description" content="[^"]*"\s*\/?>/, render: page => `<meta name="twitter:description" content="${escapeHtml(page.description)}" />` },
]

const JSON_LD = /\s*<script type="application\/ld\+json">[\s\S]*?<\/script>/

/**
 * index.html with one page's head tags swapped in. The homepage's JSON-LD
 * (WebApplication + Organization) describes the site, not a legal page, so it
 * is kept on `/` only.
 */
export function renderPageHtml(indexHtml, page) {
  let html = indexHtml
  for (const tag of HEAD_TAGS) {
    if (!tag.pattern.test(html)) throw new Error(`seoRoutes: index.html has no ${tag.name} tag to replace`)
    html = html.replace(tag.pattern, () => tag.render(page))
  }
  if (page.path !== '/') html = html.replace(JSON_LD, '')
  return html
}

export function buildSitemapXml(pages = PUBLIC_PAGES) {
  const urls = pages.map(page => [
    '  <url>',
    `    <loc>${canonicalUrlFor(page.path)}</loc>`,
    `    <changefreq>${page.changefreq}</changefreq>`,
    `    <priority>${page.priority}</priority>`,
    '  </url>',
  ].join('\n'))
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...urls,
    '</urlset>',
    '',
  ].join('\n')
}

function upsertHeadTag(selector, create, attr, value) {
  let el = document.head.querySelector(selector)
  if (!el) {
    el = create()
    document.head.appendChild(el)
  }
  el.setAttribute(attr, value)
}

/**
 * Point the live document's head at `pathname`. Pages that compute their own
 * title (a set spoiler page names its set) set it in their own effect, which
 * runs after this one and wins.
 */
export function applyRouteHead(pathname) {
  if (typeof document === 'undefined') return
  const page = findPageMeta(pathname)
  const home = PUBLIC_PAGES[0]
  document.title = page ? page.title : DEFAULT_TITLE
  const canonical = canonicalUrlFor(isIndexablePath(pathname) ? pathname : '/')
  upsertHeadTag('link[rel="canonical"]', () => {
    const link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    return link
  }, 'href', canonical)
  upsertHeadTag('meta[property="og:url"]', () => {
    const meta = document.createElement('meta')
    meta.setAttribute('property', 'og:url')
    return meta
  }, 'content', canonical)
  upsertHeadTag('meta[name="description"]', () => {
    const meta = document.createElement('meta')
    meta.setAttribute('name', 'description')
    return meta
  }, 'content', (page || home).description)
}
